import React, { useEffect, useRef } from 'react';

// ── Types ──────────────────────────────────────────────────────────────────

export type AlertVariant = 'success' | 'error' | 'warning' | 'info';

interface BaseAlert {
  variant: AlertVariant;
  message: string;
}

export interface ConfirmAlert extends BaseAlert {
  kind: 'confirm';
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface ToastAlert extends BaseAlert {
  kind: 'toast';
  /** ms before auto-dismiss; 0 = never (default 4000) */
  duration?: number;
  onDismiss: () => void;
}

export type AlertConfig = ConfirmAlert | ToastAlert;

// ── Visual config per variant ──────────────────────────────────────────────

const theme: Record<AlertVariant, { bg: string; border: string; icon: string; text: string; btnBg: string }> = {
  success: { bg: '#f0fdf4', border: '#86efac', icon: '✓',  text: '#166534', btnBg: '#16a34a' },
  error:   { bg: '#fff1f2', border: '#fca5a5', icon: '✕',  text: '#991b1b', btnBg: '#dc2626' },
  warning: { bg: '#fffbeb', border: '#fcd34d', icon: '⚠',  text: '#92400e', btnBg: '#d97706' },
  info:    { bg: '#eff6ff', border: '#93c5fd', icon: 'ℹ',  text: '#1e40af', btnBg: '#2563eb' },
};

// ── Component ──────────────────────────────────────────────────────────────

export default function PageAlert({ alert }: { alert: AlertConfig }) {
  const ref = useRef<HTMLDivElement>(null);
  const t   = theme[alert.variant];

  // Scroll into view when mounted
  useEffect(() => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  // Auto-dismiss for toasts
  useEffect(() => {
    if (alert.kind !== 'toast') return;
    const ms = alert.duration ?? 4000;
    if (ms === 0) return;
    const timer = setTimeout(alert.onDismiss, ms);
    return () => clearTimeout(timer);
  }, [alert]);

  return (
    <div
      ref={ref}
      role={alert.kind === 'confirm' ? 'alertdialog' : 'status'}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 12,
        padding: '14px 18px', borderRadius: 10,
        border: `1px solid ${t.border}`, background: t.bg,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        animation: 'slideDown 0.2s ease',
      }}
    >
      {/* Icon */}
      <span style={{
        width: 28, height: 28, borderRadius: '50%', background: t.btnBg,
        color: '#fff', fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {t.icon}
      </span>

      {/* Message */}
      <span style={{ flex: 1, fontSize: 14, color: t.text, lineHeight: 1.5, paddingTop: 3 }}>
        {alert.message}
      </span>

      {/* Actions */}
      {alert.kind === 'confirm' && (
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            style={btn.secondary}
            onClick={alert.onCancel}
            disabled={alert.loading}
          >
            {alert.cancelLabel ?? 'Cancelar'}
          </button>
          <button
            style={{ ...btn.primary, background: t.btnBg }}
            onClick={alert.onConfirm}
            disabled={alert.loading}
          >
            {alert.loading ? 'Procesando…' : (alert.confirmLabel ?? 'Confirmar')}
          </button>
        </div>
      )}

      {alert.kind === 'toast' && (
        <button
          style={btn.close}
          aria-label="Cerrar"
          onClick={alert.onDismiss}
        >
          ×
        </button>
      )}
    </div>
  );
}

// ── Hook for managing alert state ──────────────────────────────────────────

export function usePageAlert() {
  const [alert, setAlert] = React.useState<AlertConfig | null>(null);

  const showToast = (variant: AlertVariant, message: string, duration?: number) => {
    setAlert({
      kind: 'toast',
      variant,
      message,
      duration,
      onDismiss: () => setAlert(null),
    });
  };

  const showConfirm = (
    message: string,
    onConfirm: () => void,
    opts?: { variant?: AlertVariant; confirmLabel?: string; cancelLabel?: string },
  ) => {
    setAlert({
      kind: 'confirm',
      variant: opts?.variant ?? 'warning',
      message,
      confirmLabel: opts?.confirmLabel,
      cancelLabel:  opts?.cancelLabel,
      onConfirm: () => {
        setAlert(prev => prev?.kind === 'confirm' ? { ...prev, loading: true } : prev);
        onConfirm();
      },
      onCancel: () => setAlert(null),
    });
  };

  const dismissAlert = () => setAlert(null);

  return { alert, showToast, showConfirm, dismissAlert };
}

// ── Styles ─────────────────────────────────────────────────────────────────

const btn: Record<string, React.CSSProperties> = {
  primary: {
    padding: '6px 14px', color: '#fff', border: 'none',
    borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  secondary: {
    padding: '6px 14px', background: '#fff', color: '#475569',
    border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  close: {
    background: 'none', border: 'none', fontSize: 20, lineHeight: 1,
    color: '#94a3b8', cursor: 'pointer', padding: '0 2px', flexShrink: 0,
  },
};
